/**
 * @module Services/Bff/Presenters/Catalog
 * @desc Catalog Presenter
 *
 */

import { formatMoney } from 'core/utilities';

import { ProfitabilityProductType, type ProductResponse } from '../../@types';

import type { Catalog } from 'core/domains';

type Cycles = ProductResponse['data']['items'][number]['cycles'][number];
type Departments = ProductResponse['data']['items'][number]['departments'];

function pricePresenter(cycles: Cycles[]) {
  const region = cycles[0].regions[0];

  const { priceSpecification, resellerPriceSpecification } = region;
  const { price: priceReseller, profit } = resellerPriceSpecification;
  const { discount, maxPrice, percent, price } = priceSpecification;

  return {
    price: {
      discount: formatMoney(discount),
      discountNum: discount,
      maxPrice: formatMoney(maxPrice),
      maxPriceNum: maxPrice,
      percent: `${percent}%`,
      percentNum: percent,
      price: formatMoney(priceReseller),
      priceNum: priceReseller,
    },
    resellerPrice: {
      price: formatMoney(price),
      priceNum: price,
      profit: formatMoney(profit),
      profitNum: profit,
    },
    onSale: !!discount,
  };
}

function departmentsPresenter(departments: Departments) {
  if (departments) {
    const [departament] = departments.flatMap(({ categories, name: departmentName }) =>
      categories.map(({ name: categoryName }) => ({
        department: departmentName,
        category: categoryName,
      })),
    );

    return departament;
  }

  return {} as Pick<Catalog['data'][number], 'department' | 'category'>;
}

function productsPresenter(data: ProductResponse['data'], payload?: any): Catalog['data'] {
  const { items } = data;

  if (!items.length) {
    return [];
  }

  return items.map(
    ({
      badge = [],
      brand,
      organization,
      cycles,
      departments,
      details = {},
      imageObjects,
      inventory,
      name,
      productType = '',
      sku,
      slugName,
      priceWrapper,
      hasSimilar,
      resellerOnly,
    }) => {
      const hasImages = imageObjects.length > 0;
      const featuredImage = imageObjects.find(({ featured }) => featured);
      const fallbackImage = hasImages ? imageObjects[0].large : '';
      const listName = () => {
        if (payload?.geraCategory) {
          return `gera-category-${payload.geraCategory}`;
        }

        if (payload?.tag) {
          return payload.tag;
        }

        if (payload?.department) {
          return payload.department;
        }

        if (typeof payload === 'string') {
          return `busca-${payload}`;
        }

        return '';
      };
      return {
        itemListName: listName(),
        brand: brand?.name ?? organization.name,
        description: details?.shortDescription ?? '',
        image: featuredImage ? featuredImage.large : fallbackImage,
        hasSimilar,
        inventory: inventory?.managed?.qtd || 0,
        isAvailable: inventory?.managed?.available || false,
        isCustomCombo: productType === 'CUSTOM_KIT',
        name,
        productCode: sku,
        productType,
        resellerOnly: cycles[0].profitabilityProductType === ProfitabilityProductType.RESELLER,
        slugName: slugName || '',
        tags: badge,
        url: `/${slugName}`,
        ...pricePresenter(cycles),
        ...departmentsPresenter(departments),
        priceWrapper,
      };
    },
  );
}

function paginationPresenter(pagination: ProductResponse['pagination']): Catalog['pagination'] {
  const { currentPage, pageSize, totalPages, totalSize } = pagination!;
  const nextPage = totalPages > currentPage ? currentPage + 1 : currentPage;

  return {
    data: {
      currentPage,
      hasNextPage: nextPage !== currentPage,
      itemsPerPage: pageSize,
      nextPage,
      totalItems: totalSize,
      totalPages,
    },
  } as Catalog['pagination'];
}

/**
 * Transform the response of the catalog service to a catalog
 *
 */
export function catalogPresenter(
  { data, pagination }: ProductResponse,
  payload?: any,
): Pick<Catalog, 'data' | 'pagination'> {
  return {
    data: productsPresenter(data, payload),
    pagination: paginationPresenter(pagination),
  };
}
